import React, { useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
	Checkbox,
	CircularProgress,
	Typography,
	Box,
	Chip,
	Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import apiClient from '../services/apiClient';
import notify from '../services/notify';

interface ManageCustomersModalProps {
	isOpen: boolean;
	onClose: () => void;
	customerGroup: any;
	onCustomersUpdated: () => void;
}

interface Customer {
	id: number;
	name: string;
	email?: string;
	phone?: string;
	address?: string;
	customer_group_ids?: string;
}

export default function ManageCustomersModal({
	isOpen,
	onClose,
	customerGroup,
	onCustomersUpdated,
}: ManageCustomersModalProps) {
	const [searchTerm, setSearchTerm] = useState('');
	const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
	const [customersInGroup, setCustomersInGroup] = useState<Customer[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<number>>(new Set());
	const [loadingCustomerIds, setLoadingCustomerIds] = useState<Set<number>>(new Set());

	useEffect(() => {
		if (isOpen) {
			fetchData();
		}
	}, [isOpen, customerGroup]);

	const fetchData = async () => {
		setIsLoading(true);
		try {
			// Fetch all customers and customers in this group in parallel
			const [allCustomersResponse, groupCustomersResponse] = await Promise.all([
				apiClient.get('/customers'),
				customerGroup?.id
					? apiClient.get(`/customer-groups/${customerGroup.id}/customers`)
					: Promise.resolve({ data: [] }),
			]);

			const allCustomersData = allCustomersResponse.data || [];
			const groupCustomersData = groupCustomersResponse.data || [];

			setAllCustomers(allCustomersData);
			setCustomersInGroup(groupCustomersData);

			// Initialize selected customers with those already in the group
			setSelectedCustomerIds(new Set(groupCustomersData.map((c: Customer) => c.id)));
		} catch (error) {
			console.error('Error fetching customers:', error);
			notify('error', 'Failed to load customers');
		} finally {
			setIsLoading(false);
		}
	};

	const handleCustomerToggle = async (customerId: number) => {
		const isCurrentlySelected = selectedCustomerIds.has(customerId);

		// Add to loading state
		setLoadingCustomerIds((prev) => new Set(prev).add(customerId));

		try {
			if (isCurrentlySelected) {
				// Remove customer from group
				await removeCustomerFromGroup(customerId);
				const newSelected = new Set(selectedCustomerIds);
				newSelected.delete(customerId);
				setSelectedCustomerIds(newSelected);

				// Remove from customersInGroup state
				setCustomersInGroup((prev) => prev.filter((c) => c.id !== customerId));
			} else {
				// Add customer to group
				await addCustomerToGroup(customerId);
				const newSelected = new Set(selectedCustomerIds);
				newSelected.add(customerId);
				setSelectedCustomerIds(newSelected);

				// Add to customersInGroup state
				const customer = allCustomers.find((c) => c.id === customerId);
				if (customer) {
					setCustomersInGroup((prev) => [...prev, customer]);
				}
			}

			onCustomersUpdated(); // Refresh parent component
		} catch (error: any) {
			console.error('Error updating customer assignment:', error);
			notify('error', error.response?.data?.message || 'Failed to update customer assignment');
		} finally {
			// Remove from loading state
			setLoadingCustomerIds((prev) => {
				const newSet = new Set(prev);
				newSet.delete(customerId);
				return newSet;
			});
		}
	};

	const removeCustomerFromGroup = async (customerId: number) => {
		// Find the customer to get their current group IDs
		const customer = allCustomers.find((c) => c.id === customerId);
		if (!customer || !customer.customer_group_ids) return;

		// Parse current group IDs and remove this group
		const currentGroupIds = customer.customer_group_ids
			.split(',')
			.map((id) => parseInt(id.trim()))
			.filter((id) => !isNaN(id) && id !== customerGroup.id);

		// Update customer with new group IDs (or null if empty)
		const newGroupIds = currentGroupIds.length > 0 ? currentGroupIds.join(',') : null;

		await apiClient.update(`/customers/${customerId}`, {
			customer_group_ids: newGroupIds,
		});
	};

	const addCustomerToGroup = async (customerId: number) => {
		// Find the customer to get their current group IDs
		const customer = allCustomers.find((c) => c.id === customerId);
		if (!customer) return;

		let currentGroupIds: number[] = [];
		if (customer.customer_group_ids) {
			currentGroupIds = customer.customer_group_ids
				.split(',')
				.map((id) => parseInt(id.trim()))
				.filter((id) => !isNaN(id));
		}

		// Add the new group ID if it's not already there
		if (!currentGroupIds.includes(customerGroup.id)) {
			currentGroupIds.push(customerGroup.id);
		}

		const newGroupIds = currentGroupIds.join(',');

		await apiClient.update(`/customers/${customerId}`, {
			customer_group_ids: newGroupIds,
		});
	};

	const handleClose = () => {
		onClose();
	};

	// Filter customers based on search term
	const filteredCustomers = allCustomers.filter(
		(customer) =>
			customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			(customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
			(customer.phone && customer.phone.includes(searchTerm))
	);

	// Separate customers into those in group and those not in group for better organization
	const customersInGroupFiltered = filteredCustomers.filter((c) => customersInGroup.some((gc) => gc.id === c.id));
	const customersNotInGroupFiltered = filteredCustomers.filter((c) => !customersInGroup.some((gc) => gc.id === c.id));

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				style: {
					minHeight: '600px',
					maxHeight: '80vh',
				},
			}}
		>
			<DialogTitle>
				<Box display="flex" alignItems="center" gap={1}>
					<PersonIcon />
					<span>Manage Customers - {customerGroup?.name}</span>
				</Box>
			</DialogTitle>

			<DialogContent>
				{/* Search Field */}
				<TextField
					fullWidth
					placeholder="Search customers by name, email, or phone..."
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					InputProps={{
						startAdornment: <SearchIcon style={{ marginRight: '8px', color: '#9ca3af' }} />,
					}}
					style={{ marginBottom: '20px' }}
				/>

				{isLoading ? (
					<Box display="flex" justifyContent="center" py={4}>
						<CircularProgress />
					</Box>
				) : (
					<>
						{/* Summary */}
						<Box mb={2}>
							<Chip
								label={`${selectedCustomerIds.size} customer${
									selectedCustomerIds.size !== 1 ? 's' : ''
								} selected`}
								color="primary"
								variant="outlined"
							/>
						</Box>

						{/* Customers currently in group */}
						{customersInGroupFiltered.length > 0 && (
							<>
								<Typography
									variant="subtitle2"
									gutterBottom
									style={{ fontWeight: 'bold', color: '#374151' }}
								>
									Currently in "{customerGroup?.name}" ({customersInGroupFiltered.length})
								</Typography>
								<List dense style={{ maxHeight: '200px', overflow: 'auto', marginBottom: '16px' }}>
									{customersInGroupFiltered.map((customer) => (
										<ListItem
											key={customer.id}
											component="div"
											style={{
												backgroundColor: selectedCustomerIds.has(customer.id)
													? '#f0f9ff'
													: '#fff3cd',
												borderRadius: '4px',
												marginBottom: '2px',
												border: selectedCustomerIds.has(customer.id)
													? '1px solid #0ea5e9'
													: '1px solid #fbbf24',
												cursor: 'pointer',
											}}
											onClick={() => handleCustomerToggle(customer.id)}
										>
											<ListItemText
												primary={customer.name}
												secondary={
													<span>
														{customer.email && <span>{customer.email}</span>}
														{customer.email && customer.phone && <span> • </span>}
														{customer.phone && <span>{customer.phone}</span>}
														{!selectedCustomerIds.has(customer.id) && (
															<Typography
																component="span"
																style={{
																	color: '#d97706',
																	fontSize: '12px',
																	fontStyle: 'italic',
																}}
															>
																{' '}
																• Will be removed
															</Typography>
														)}
													</span>
												}
											/>
											<ListItemSecondaryAction>
												{loadingCustomerIds.has(customer.id) ? (
													<CircularProgress size={20} />
												) : (
													<Checkbox
														edge="end"
														checked={selectedCustomerIds.has(customer.id)}
														onChange={() => handleCustomerToggle(customer.id)}
														color="primary"
													/>
												)}
											</ListItemSecondaryAction>
										</ListItem>
									))}
								</List>
								<Divider style={{ margin: '16px 0' }} />
							</>
						)}

						{/* Other customers */}
						<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 'bold', color: '#374151' }}>
							Other Customers ({customersNotInGroupFiltered.length})
						</Typography>
						<List dense style={{ maxHeight: '300px', overflow: 'auto' }}>
							{customersNotInGroupFiltered.length > 0 ? (
								customersNotInGroupFiltered.map((customer) => (
									<ListItem
										key={customer.id}
										component="div"
										style={{
											backgroundColor: selectedCustomerIds.has(customer.id)
												? '#f0f9ff'
												: 'transparent',
											borderRadius: '4px',
											marginBottom: '2px',
											cursor: 'pointer',
										}}
										onClick={() => handleCustomerToggle(customer.id)}
									>
										<ListItemText
											primary={customer.name}
											secondary={
												<span>
													{customer.email && <span>{customer.email}</span>}
													{customer.email && customer.phone && <span> • </span>}
													{customer.phone && <span>{customer.phone}</span>}
													{customer.customer_group_ids && (
														<Typography
															component="span"
															style={{ color: '#6b7280', fontSize: '12px' }}
														>
															{' '}
															• In other groups
														</Typography>
													)}
													{selectedCustomerIds.has(customer.id) && (
														<Typography
															component="span"
															style={{
																color: '#059669',
																fontSize: '12px',
																fontStyle: 'italic',
															}}
														>
															{' '}
															• Will be added
														</Typography>
													)}
												</span>
											}
										/>
										<ListItemSecondaryAction>
											{loadingCustomerIds.has(customer.id) ? (
												<CircularProgress size={20} />
											) : (
												<Checkbox
													edge="end"
													checked={selectedCustomerIds.has(customer.id)}
													onChange={() => handleCustomerToggle(customer.id)}
													color="primary"
												/>
											)}
										</ListItemSecondaryAction>
									</ListItem>
								))
							) : (
								<Typography
									variant="body2"
									color="textSecondary"
									style={{ padding: '20px', textAlign: 'center' }}
								>
									{searchTerm
										? 'No customers found matching your search.'
										: 'All customers are already in this group.'}
								</Typography>
							)}
						</List>
					</>
				)}
			</DialogContent>

			<DialogActions style={{ padding: '16px 24px' }}>
				<Button onClick={handleClose} variant="contained">
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
}
